import { CenterFocusWeak as CentreIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Handle, NodeProps, Position } from "@xyflow/react";
import React, { useState } from "react";

import { monospaceStyle } from "../../theme";
import { ChainCard, ChainMaker, ChainPath } from "../../types/valueChain";
import {
  AREA_HEAD,
  AREA_PAD,
  AreaBox,
  ChainNode,
  pluralNature,
} from "./chainLayout";
import {
  CollapsedRow,
  FocusSummary,
  PathColour,
  cardName,
  typicalOnly,
} from "./chainModel";
import { useChainActions } from "./chainActions";
import { SINGLE_MAKER, isRuleFlag, useChainLens } from "./chainLens";
import {
  FLAGGED_BORDER,
  FlagChip,
  JurisdictionBlock,
  MakerLensMark,
  SingleMakerChip,
} from "./LensBlocks";
import { PATH_COLOUR_VALUES } from "./pathColours";
import { useTreeActions } from "./treeActions";
import { BRANCH_HANDLE_Y, BRANCH_HEAD, BRANCH_RAIL } from "./treeLayout";
import {
  CARRIED_SUBSYSTEM,
  OTHER_SUBSYSTEM,
  Subsystem,
  TreeCardInfo,
  isCarried,
} from "./treeModel";
import { isOpened } from "./valueChainState";

// The text of the accent: a proven maker's chip, the proven block's head
export const ACCENT_TEXT = "#9dc1ff";

// The dashed outline of a collapsed row and of the parts stub
export const DASHED_BORDER = "rgba(255, 255, 255, 0.18)";

// Maker chips a block shows before the rest is counted in a "+N"
export const MAKER_CHIP_LIMIT = 3;

// The opacity of a card a maker highlight does not keep lit
const DIMMED_OPACITY = 0.3;

// A control on a card: pressing it neither drags the card nor pans the canvas
const CONTROL_CLASS = "nodrag nopan";

// The control that makes a card the centre of the chain: a small mark beside
// its name, quiet until pointed at
export const CentreButton: React.FC<{ card: ChainCard; name: string }> = ({
  card,
  name,
}) => {
  const { onCentre } = useChainActions();
  const label = `Centre the chain on ${name}`;
  return (
    <Tooltip title={label}>
      <IconButton
        size="small"
        aria-label={label}
        className={CONTROL_CLASS}
        onClick={() => onCentre({ type: card.type, id: card.id })}
        sx={{
          p: "2px",
          mt: "-2px",
          flexShrink: 0,
          alignSelf: "flex-start",
          color: "text.disabled",
          "&:hover, &:focus-visible": { color: "text.primary" },
        }}
      >
        <CentreIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
};

// "1 product", "2 products": the number is the field, the word follows it
const counted = (count: number, noun: string, plural = `${noun}s`): string =>
  `${count} ${count === 1 ? noun : plural}`;

// A number, in the monospace face; the words around it stay in the text face
const Num: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={monospaceStyle}>
    {children}
  </Box>
);

// A count and its noun, the number in the monospace face
const Counted: React.FC<{ count: number; noun: string; plural?: string }> = ({
  count,
  noun,
  plural = `${noun}s`,
}) => (
  <>
    <Num>{count}</Num> {count === 1 ? noun : plural}
  </>
);

// Parts of one line, set apart by a middle dot
const Dotted: React.FC<{ parts: React.ReactNode[] }> = ({ parts }) => (
  <>
    {parts.map((part, index) => (
      <React.Fragment key={index}>
        {index > 0 && " · "}
        {part}
      </React.Fragment>
    ))}
  </>
);

// The countries a card's makers are at home in, the unknown bucket left out
const countriesOf = (card: ChainCard): number =>
  card.countries.filter((entry) => entry.country !== null).length;

// The makers of a card proven inside its neighbour nearer the root
const provenOf = (card: ChainCard): ChainMaker[] =>
  card.makers.filter((maker) => maker.standing === "proven");

// The evidence line of a card: what is on record of it
export const CardEvidence: React.FC<{ card: ChainCard }> = ({ card }) => (
  <Typography
    variant="caption"
    component="div"
    color="text.secondary"
    aria-label="Card evidence"
  >
    <Dotted
      parts={[
        <Counted count={card.product_count} noun="product" />,
        <Counted count={card.makers.length} noun="maker" />,
        <Counted count={countriesOf(card)} noun="country" plural="countries" />,
        <Counted count={card.articles} noun="article" />,
      ]}
    />
  </Typography>
);

// The words of one path of a card: direct, or via its intermediate card
export const pathName = (path: ChainPath): string =>
  path.via ? `via ${path.via_label ?? path.via}` : "direct";

// The ways a host card is reached from the root on one line, each in the
// colour its line is drawn in, with its stated pairs; a path with none, on
// typical-host rows alone, with those rows behind a tilde
const PathLine: React.FC<{
  paths: ChainPath[];
  pathColours: ReadonlyMap<string, PathColour>;
}> = ({ paths, pathColours }) => (
  <Box
    component="ul"
    aria-label="Paths"
    sx={{
      m: 0,
      p: 0,
      listStyle: "none",
      display: "flex",
      flexWrap: "wrap",
      columnGap: 0.5,
      fontSize: 11,
      lineHeight: 1.4,
      color: "text.secondary",
    }}
  >
    {paths.map((path, index) => {
      const colour = path.via ? (pathColours.get(path.via) ?? null) : null;
      const typical = typicalOnly(path);
      return (
        <Box
          component="li"
          key={path.via ?? "direct"}
          aria-label={pathName(path)}
          title={`${
            typical
              ? counted(path.typical, "typical row")
              : counted(path.pairs, "pair")
          } · ${counted(path.articles, "article")}`}
          data-path-colour={colour ?? (path.via ? "none" : "direct")}
          sx={{
            color: colour
              ? PATH_COLOUR_VALUES[colour]
              : path.via
                ? "text.secondary"
                : "link.main",
          }}
        >
          {index > 0 && (
            <Box component="span" sx={{ color: "text.secondary", mr: 0.5 }}>
              ·
            </Box>
          )}
          {pathName(path)}{" "}
          <Num>{typical ? `~${path.typical}` : path.pairs}</Num>
        </Box>
      );
    })}
  </Box>
);

const nameOf = (name: string | null): string => name ?? "Unnamed product";

// What a maker chip says on hover: its role, its products, the pair that
// proves it
const makerTitle = (maker: ChainMaker): string =>
  [
    `${maker.role}, ${counted(maker.product_count, "product")}`,
    ...(maker.proven_by
      ? [
          `${nameOf(maker.proven_by.part.name)} in ${nameOf(maker.proven_by.host.name)}`,
        ]
      : []),
  ].join("; ");

const chipSx = (accent: boolean) => (theme: Theme) => ({
  height: 20,
  fontSize: "11.5px",
  maxWidth: 200,
  ...(accent
    ? {
        borderColor: alpha(theme.palette.accent.main, 0.45),
        color: ACCENT_TEXT,
        backgroundColor: alpha(theme.palette.accent.main, 0.1),
      }
    : {}),
});

// One maker of a card as a chip that opens the organization: in the accent
// when it is proven inside the neighbour; with the host product that proves
// it beside it when asked
const MakerChip: React.FC<{
  maker: ChainMaker;
  accent: boolean;
  withHost?: boolean;
}> = ({ maker, accent, withHost = false }) => {
  const { onOpenDetail } = useChainActions();
  const makerName = maker.organization.name ?? maker.organization.id;
  return (
    <Box
      component="li"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Chip
        size="small"
        variant="outlined"
        label={makerName}
        title={makerTitle(maker)}
        aria-label={`Open the organization ${makerName}`}
        data-standing={maker.standing}
        className={CONTROL_CLASS}
        onClick={() =>
          onOpenDetail({ type: "organization", id: maker.organization.id })
        }
        sx={chipSx(accent)}
      />
      <MakerLensMark maker={maker} />
      {withHost && maker.proven_by && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 0, overflowWrap: "anywhere" }}
        >
          in {nameOf(maker.proven_by.host.name)}
        </Typography>
      )}
    </Box>
  );
};

// The "+N" of the makers a block does not show: it opens the card's kind,
// which lists them all
const MoreMakers: React.FC<{ count: number; name: string; onOpen: () => void }> = ({
  count,
  name,
  onOpen,
}) => (
  <Box component="li">
    <Link
      component="button"
      type="button"
      underline="hover"
      className={CONTROL_CLASS}
      aria-label={`${counted(count, "more maker", "more makers")}: open ${name}`}
      onClick={onOpen}
      sx={{ ...monospaceStyle, fontSize: 12, color: "text.disabled" }}
    >
      +{count}
    </Link>
  </Box>
);

// A row of maker chips, at most `MAKER_CHIP_LIMIT`, then the "+N"; one per
// line with its proving host when `withHost` is set
const MakerList: React.FC<{
  makers: ChainMaker[];
  name: string;
  onOpen: () => void;
  withHost?: boolean;
}> = ({ makers, name, onOpen, withHost = false }) => {
  const shown = makers.slice(0, MAKER_CHIP_LIMIT);
  const hidden = makers.length - shown.length;
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        p: 0,
        listStyle: "none",
        display: "flex",
        flexDirection: withHost ? "column" : "row",
        flexWrap: "wrap",
        alignItems: withHost ? "flex-start" : "center",
        gap: "5px",
      }}
    >
      {shown.map((maker) => (
        <MakerChip
          key={`${maker.organization.id}:${maker.role}`}
          maker={maker}
          accent={maker.standing === "proven"}
          withHost={withHost}
        />
      ))}
      {hidden > 0 && <MoreMakers count={hidden} name={name} onOpen={onOpen} />}
    </Box>
  );
};

// The small upper-case head of a block of a card
const BlockHead: React.FC<{ accent?: boolean; children: React.ReactNode }> = ({
  accent = false,
  children,
}) => (
  <Typography
    component="h4"
    sx={{
      m: 0,
      mt: 0.5,
      fontSize: 11,
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: accent ? ACCENT_TEXT : "text.secondary",
    }}
  >
    {children}
  </Typography>
);

// The cards a card is stated with nearer the root, in words
const neighbourWords = (card: ChainCard, neighbours: string[]): string => {
  if (neighbours.length === 0) {
    return card.side === "hosts" ? "a part" : "a host";
  }
  if (neighbours.length === 1) {
    return neighbours[0];
  }
  if (neighbours.length === 2) {
    return `${neighbours[0]} or ${neighbours[1]}`;
  }
  return `${neighbours[0]} or ${neighbours.length - 1} more`;
};

// What a card's stated products are stated with: inside its host on the
// parts side, holding its part on the hosts side, with the products no maker
// is on record for, then the typical-host rows that reach it too. A card
// reached by those rows alone says so, with the rows for a count: none of
// its products is stated.
const StatedWords: React.FC<{ card: ChainCard; neighbours: string[] }> = ({
  card,
  neighbours,
}) => {
  const near = neighbourWords(card, neighbours);
  if (card.stated_count === 0 && card.typical_count > 0) {
    return (
      <Dotted
        parts={[
          card.side === "hosts"
            ? `${near} typically built into`
            : `Typically built into ${near}`,
          <Counted count={card.typical_count} noun="row" />,
        ]}
      />
    );
  }
  return (
    <Dotted
      parts={[
        card.side === "hosts" ? `${near} stated inside` : `Stated inside ${near}`,
        <Counted count={card.stated_count} noun="product" />,
        ...(card.stated_without_maker > 0
          ? [
              <>
                <Num>{card.stated_without_maker}</Num> without a maker
              </>,
            ]
          : []),
        ...(card.typical_count > 0
          ? [
              <>
                <Num>{card.typical_count}</Num> typical
              </>,
            ]
          : []),
      ]}
    />
  );
};

// The makers of a card. The root lists them as one row of chips. Another
// card heads its proven makers with what its products are stated inside;
// opened, it lists each with the host product that proves it, then the other
// makers of the kind with the count of those proven elsewhere; closed, the
// proven and the other makers share one row.
const MakerBlocks: React.FC<{
  card: ChainCard;
  root: boolean;
  open: boolean;
  neighbours: string[];
  name: string;
  onOpen: () => void;
}> = ({ card, root, open, neighbours, name, onOpen }) => {
  const none = card.makers.length === 0 && (
    <Typography variant="caption" component="div" color="text.secondary">
      No maker on record
    </Typography>
  );
  if (root) {
    return (
      none || (
        <Box role="group" aria-label="Makers">
          <MakerList makers={card.makers} name={name} onOpen={onOpen} />
        </Box>
      )
    );
  }
  const proven = provenOf(card);
  const others = card.makers.filter((maker) => maker.standing !== "proven");
  const elsewhere = others.filter(
    (maker) => maker.standing === "proven_elsewhere",
  ).length;
  const stated =
    card.stated_count > 0 || card.typical_count > 0 || proven.length > 0;

  if (!open) {
    return (
      <Box role="group" aria-label="Makers">
        {stated && (
          <BlockHead accent>
            <StatedWords card={card} neighbours={neighbours} />
          </BlockHead>
        )}
        {none || (
          <Box sx={{ mt: 0.5 }}>
            <MakerList makers={card.makers} name={name} onOpen={onOpen} />
          </Box>
        )}
      </Box>
    );
  }
  return (
    <>
      {stated && (
        <Box role="group" aria-label="Proven">
          <BlockHead accent>
            <StatedWords card={card} neighbours={neighbours} />
          </BlockHead>
          {proven.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <MakerList makers={proven} name={name} onOpen={onOpen} withHost />
            </Box>
          )}
        </Box>
      )}
      {others.length > 0 && (
        <Box role="group" aria-label="Other makers">
          <BlockHead>
            Other makers of this kind · <Num>{others.length}</Num>
            {elsewhere > 0 && (
              <Box
                component="span"
                sx={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}
              >
                {" "}
                (<Num>{elsewhere}</Num> proven elsewhere)
              </Box>
            )}
          </BlockHead>
          <Box sx={{ mt: 0.5 }}>
            <MakerList makers={others} name={name} onOpen={onOpen} />
          </Box>
        </Box>
      )}
      {none}
    </>
  );
};

// The classes a card's products are stated as, in one line that opens into
// their chips in place. A stated class opens as a card of its own; the
// card's own class is the card and does not open.
const StatedClasses: React.FC<{ card: ChainCard; name: string }> = ({
  card,
  name,
}) => {
  const { opened, onToggle } = useChainActions();
  const [shown, setShown] = useState(false);
  const others = card.classes.filter(
    (stated) => stated.category.id !== card.category_id,
  );
  if (others.length === 0) {
    return null;
  }
  // A class opened as a card keeps the chips shown
  const anyOpen = others.some((stated) =>
    isOpened(opened, { type: "class", id: stated.category.id }),
  );
  const expanded = shown || anyOpen;
  const names = card.classes.map(
    (stated) => stated.category.name ?? stated.category.id,
  );
  return (
    <Box role="group" aria-label="Stated classes">
      <Typography variant="caption" component="div" color="text.secondary">
        Stated as{" "}
        <Link
          component="button"
          type="button"
          underline="hover"
          className={CONTROL_CLASS}
          aria-expanded={expanded}
          aria-label={`The classes ${name} is stated as`}
          onClick={() => setShown(!expanded)}
          sx={{ fontSize: "inherit", verticalAlign: "baseline" }}
        >
          <Num>{card.classes.length}</Num>{" "}
          {card.classes.length === 1 ? "class" : "classes"}{" "}
          {expanded ? "▴" : "▾"}
        </Link>
        {!expanded &&
          ` · ${names.slice(0, 2).join(", ")}${names.length > 2 ? " …" : ""}`}
      </Typography>
      {expanded && (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
          {card.classes.map((stated) => {
            const className = stated.category.name ?? stated.category.id;
            const label = (
              <>
                {className} <Num>{stated.product_count}</Num>
              </>
            );
            // The card's own class is the card: it does not open
            if (stated.category.id === card.category_id) {
              return (
                <Chip
                  key={stated.category.id}
                  label={label}
                  size="small"
                  sx={{ height: 20, fontSize: "11.5px" }}
                />
              );
            }
            const ref = { type: "class" as const, id: stated.category.id };
            const open = isOpened(opened, ref);
            return (
              <Chip
                key={stated.category.id}
                label={label}
                size="small"
                variant="outlined"
                aria-pressed={open}
                aria-label={`${open ? "Close" : "Open"} the class ${className}`}
                onClick={() => onToggle(ref)}
                className={CONTROL_CLASS}
                sx={(theme: Theme) => ({
                  height: 20,
                  fontSize: "11.5px",
                  ...(open
                    ? {
                        borderColor: theme.palette.accent.main,
                        color: ACCENT_TEXT,
                      }
                    : {}),
                })}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// A link-styled control of a card
const CardLink: React.FC<{
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  expanded?: boolean;
  colour?: string;
}> = ({ label, onClick, children, expanded, colour }) => (
  <Link
    component="button"
    type="button"
    underline="hover"
    className={CONTROL_CLASS}
    aria-label={label}
    aria-expanded={expanded}
    onClick={onClick}
    sx={{ fontSize: "inherit", ...(colour ? { color: colour } : {}) }}
  >
    {children}
  </Link>
);

// The expander of a card of the parts side, worded by the kinds beyond it:
// opened, the kinds it opened; closed, the kinds it holds, or plainly its
// parts at the edge of the read; nothing to open when no part is stated
// inside it within the read
const PartsExpander: React.FC<{
  card: ChainCard;
  name: string;
  partsDrawn: number;
}> = ({ card, name, partsDrawn }) => {
  const { opened, loading, onToggle, partsDepth } = useChainActions();
  const self = { type: card.type, id: card.id };
  const expanded = isOpened(opened, self);
  const pending = isOpened(loading, self);
  const beyond = card.kinds_beyond ?? 0;
  const spinner = pending && (
    <CircularProgress size={10} sx={{ ml: 0.75 }} aria-label="Opening" />
  );
  if (expanded) {
    const kinds = Math.max(beyond, partsDrawn);
    return (
      <CardLink
        label={`Close the parts of ${name}`}
        expanded
        onClick={() => onToggle(self)}
        colour="text.secondary"
      >
        ▾{" "}
        {kinds > 0 ? (
          <>
            <Counted count={kinds} noun="kind of part" plural="kinds of part" />{" "}
            opened
          </>
        ) : (
          "no part on record"
        )}
        {spinner}
      </CardLink>
    );
  }
  if (beyond === 0 && card.step < partsDepth) {
    return (
      <Typography component="span" sx={{ fontSize: "inherit", color: "text.disabled" }}>
        no part stated
      </Typography>
    );
  }
  return (
    <CardLink
      label={`Open the parts of ${name}`}
      expanded={false}
      onClick={() => onToggle(self)}
    >
      ▸{" "}
      {beyond > 0 ? (
        <Counted count={beyond} noun="kind of part" plural="kinds of part" />
      ) : (
        "Parts"
      )}
      {spinner}
    </CardLink>
  );
};

// The foot of a card: a control or a note on the left, the link that opens
// the card's kind on the right
const CardFoot: React.FC<{
  name: string;
  onOpen: () => void;
  children?: React.ReactNode;
}> = ({ name, onOpen, children }) => (
  <Box
    sx={{
      mt: "auto",
      pt: 0.5,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 1,
      fontSize: "12.5px",
    }}
  >
    <Box sx={{ minWidth: 0 }}>{children}</Box>
    <CardLink label={`Open ${name}`} onClick={onOpen}>
      Open ›
    </CardLink>
  </Box>
);

// The root's switch to the other side: what it is built into when its
// parts are read, its parts when its hosts are
const RootSwitch: React.FC<{ name: string }> = ({ name }) => {
  const { direction, onDirectionChange } = useChainActions();
  if (direction === "parts") {
    return (
      <CardLink
        label={`Show what ${name} is built into`}
        onClick={() => onDirectionChange("hosts")}
      >
        ← Built into
      </CardLink>
    );
  }
  if (direction === "hosts") {
    return (
      <CardLink
        label={`Show what is stated inside ${name}`}
        onClick={() => onDirectionChange("parts")}
      >
        Parts →
      </CardLink>
    );
  }
  return null;
};

// What the focus card says of the hosts it goes into
const FocusLine: React.FC<{ summary: FocusSummary }> = ({ summary }) => {
  const nature = summary.nature ?? "host";
  return (
    <Typography variant="caption" component="div" aria-label="Goes into">
      Goes into{" "}
      <Box component="b" sx={{ fontWeight: 600 }}>
        <Counted count={summary.furthest} noun="kind" /> of {nature}
      </Box>
      {summary.modules > 0 && (
        <>
          : via <Counted count={summary.modules} noun="kind" /> of module
        </>
      )}
      {summary.direct && (
        <>
          {summary.modules > 0 ? ", or " : ": "}
          <Box component="b" sx={{ fontWeight: 600, color: "link.main" }}>
            directly
          </Box>
        </>
      )}
    </Typography>
  );
};

// The hosts nearer than the furthest column that no path runs through
const FocusFoot: React.FC<{ summary: FocusSummary }> = ({ summary }) =>
  summary.others > 0 ? (
    <Typography component="span" sx={{ fontSize: "inherit", color: "text.disabled" }}>
      +<Num>{summary.others}</Num> {summary.others === 1 ? "host" : "hosts"} not
      drawn as {pluralNature(summary.nature ?? "host")}
    </Typography>
  ) : null;

// The kinds and pairs beyond a card of the hosts side
const HostsBeyond: React.FC<{ card: ChainCard; colour?: string }> = ({
  card,
  colour,
}) =>
  card.kinds_beyond ? (
    <Typography
      component="span"
      sx={{
        fontSize: "inherit",
        fontWeight: colour ? 500 : 400,
        color: colour ?? "text.secondary",
      }}
    >
      ← <Counted count={card.kinds_beyond} noun="kind" /> ·{" "}
      <Counted count={card.pairs_beyond ?? 0} noun="pair" />
    </Typography>
  ) : null;

// One card of the chain: a kind, or a class with no kind above it. The root
// carries the accent border, a card opened or open in the overlay the accent
// ring; an intermediate module of the hosts side carries its path colour on
// its left edge; a card a maker highlight does not keep lit is dimmed. A
// host in the furthest column of the focus view is drawn compact under the
// makers lens: its name and its paths.
const ChainCardView: React.FC<{
  card: ChainCard;
  root: boolean;
  dimmed: boolean;
  pathColour: PathColour | null;
  pathColours: ReadonlyMap<string, PathColour>;
  neighbours: string[];
  compact: boolean;
  summary: FocusSummary | null;
  partsDrawn: number;
}> = ({
  card,
  root,
  dimmed,
  pathColour,
  pathColours,
  neighbours,
  compact: compactColumn,
  summary,
  partsDrawn,
}) => {
  const { opened, detail, onOpenDetail } = useChainActions();
  const { lens, flags } = useChainLens();
  const name = cardName(card);
  const self = { type: card.type, id: card.id };
  const host = card.side === "hosts";
  const onOpen = () =>
    onOpenDetail({ type: "category", id: card.category_id });

  const detailOpen =
    detail !== null &&
    (detail.type === "kind" ||
      detail.type === "class" ||
      detail.type === "category") &&
    detail.id === card.category_id;
  const ringed = isOpened(opened, self) || detailOpen;

  // Under the jurisdiction lens with a split, the card shows the split
  // instead of its makers; without one it stays as under the makers lens
  const jurisdiction = lens === "jurisdiction" && card.maker_split !== null;
  const ruleFlags = jurisdiction
    ? (flags.get(card.id) ?? []).filter(isRuleFlag)
    : [];
  const flagged = ruleFlags.length > 0;
  const compact = compactColumn && lens === "makers";
  const intermediate = pathColour !== null && !jurisdiction;

  const nameRow = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 1,
      }}
    >
      <Typography
        component="h3"
        sx={{
          m: 0,
          minWidth: 0,
          fontSize: root ? 15 : compact ? "12.5px" : "13.5px",
          fontWeight: 600,
          lineHeight: 1.35,
          overflowWrap: "anywhere",
        }}
      >
        <Link
          component="button"
          type="button"
          underline="hover"
          className={CONTROL_CLASS}
          onClick={onOpen}
          sx={{
            font: "inherit",
            color: "text.primary",
            textAlign: "left",
            verticalAlign: "baseline",
          }}
        >
          {name}
        </Link>
      </Typography>
      <Box
        sx={{ display: "flex", alignItems: "baseline", gap: "2px", minWidth: 0 }}
      >
        {/* The root is the centre already */}
        {!root && <CentreButton card={card} name={name} />}
        {!compact && (
          <Typography
            variant="caption"
            sx={{
              ...monospaceStyle,
              color: "text.disabled",
              minWidth: 0,
              textAlign: "right",
              overflowWrap: "anywhere",
            }}
          >
            {card.id}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const tags = (card.nature || card.unplaced) && (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {card.nature && (
        <Chip
          label={card.nature}
          size="small"
          variant="outlined"
          sx={{ height: 18, fontSize: "11px" }}
        />
      )}
      {card.unplaced && (
        <Chip
          label="unplaced class"
          size="small"
          variant="outlined"
          sx={{ height: 18, fontSize: "11px", color: "text.secondary" }}
        />
      )}
    </Box>
  );

  // The left of the foot: the root's switch to the other side, the focus's
  // note, the expander of a part, the kinds beyond a host
  const footLeft = root ? (
    summary && lens !== "jurisdiction" ? (
      <FocusFoot summary={summary} />
    ) : (
      <RootSwitch name={name} />
    )
  ) : host ? (
    <HostsBeyond
      card={card}
      colour={
        intermediate && pathColour ? PATH_COLOUR_VALUES[pathColour] : undefined
      }
    />
  ) : (
    <PartsExpander card={card} name={name} partsDrawn={partsDrawn} />
  );

  let body: React.ReactNode;
  if (compact) {
    body = <PathLine paths={card.paths} pathColours={pathColours} />;
  } else if (jurisdiction) {
    body = (
      <>
        <JurisdictionBlock card={card} root={root} />
        {flagged && (
          <Box
            sx={{
              mt: "auto",
              pt: 0.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {ruleFlags.map((flag) => (
                <FlagChip key={flag} flag={flag} />
              ))}
            </Box>
            <Typography
              variant="caption"
              sx={{ color: "text.disabled", whiteSpace: "nowrap" }}
            >
              <Counted count={card.product_count} noun="product" />
            </Typography>
          </Box>
        )}
        <CardFoot name={name} onOpen={onOpen}>
          {footLeft}
        </CardFoot>
      </>
    );
  } else if (intermediate) {
    body = (
      <>
        <Typography variant="caption" component="div" color="text.secondary">
          <StatedWords card={card} neighbours={neighbours} />
        </Typography>
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
          aria-label="Card evidence"
        >
          <Dotted
            parts={[
              <Counted count={card.makers.length} noun="maker" />,
              <>
                <Num>{provenOf(card).length}</Num> proven
              </>,
              <Counted
                count={countriesOf(card)}
                noun="country"
                plural="countries"
              />,
            ]}
          />
        </Typography>
        <CardFoot name={name} onOpen={onOpen}>
          {footLeft}
        </CardFoot>
      </>
    );
  } else {
    body = (
      <>
        <CardEvidence card={card} />
        {tags}
        {root && summary && <FocusLine summary={summary} />}
        {host && card.paths.some((path) => path.via !== null) && (
          <PathLine paths={card.paths} pathColours={pathColours} />
        )}
        <StatedClasses card={card} name={name} />
        <MakerBlocks
          card={card}
          root={root}
          open={ringed}
          neighbours={neighbours}
          name={name}
          onOpen={onOpen}
        />
        <CardFoot name={name} onOpen={onOpen}>
          {footLeft}
        </CardFoot>
      </>
    );
  }

  return (
    <Box
      component="article"
      aria-label={name}
      data-dimmed={dimmed ? "true" : "false"}
      data-path-colour={pathColour ?? undefined}
      data-open={ringed ? "true" : "false"}
      sx={(theme: Theme) => ({
        width: compact ? 220 : 300,
        minHeight: compact ? 56 : undefined,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: compact ? "center" : undefined,
        gap: compact ? "1px" : "6px",
        p: compact ? "7px 10px" : "12px 14px",
        backgroundColor: theme.palette.background.raised,
        border: "1px solid",
        borderColor: ringed
          ? "transparent"
          : root
            ? alpha(theme.palette.accent.main, 0.6)
            : flagged
              ? FLAGGED_BORDER
              : theme.palette.divider,
        ...(intermediate && pathColour
          ? {
              borderLeft: `3px solid ${PATH_COLOUR_VALUES[pathColour]}`,
            }
          : {}),
        borderRadius: `${theme.radius.surface}px`,
        boxShadow: ringed ? `0 0 0 1px ${theme.palette.accent.main}` : "none",
        cursor: "default",
        opacity: dimmed ? DIMMED_OPACITY : 1,
        transition: "opacity 120ms",
      })}
    >
      {nameRow}

      {/* Exactly one maker on record: the card's own flag, under every lens */}
      {!compact && card.flags.includes(SINGLE_MAKER) && (
        <SingleMakerChip country={card.single_maker_country} />
      )}

      {body}
    </Box>
  );
};

// The stub that stands for the parts side of the focus view: the root's
// parts are collapsed, the first of them by name, and a control that opens
// them. It counts nothing: the read's cards are not every kind of part on
// record.
const PartsStubView: React.FC<{
  row: CollapsedRow;
  rootName: string;
  dimmed: boolean;
}> = ({ row, rootName, dimmed }) => {
  const { onOpenParts } = useChainActions();
  const names = row.cards.map(cardName);
  return (
    <Box
      component="article"
      aria-label="Parts side"
      data-dimmed={dimmed ? "true" : "false"}
      sx={{
        width: 300,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        p: "12px 14px",
        fontSize: "12.5px",
        color: "text.secondary",
        border: `1px dashed ${DASHED_BORDER}`,
        borderRadius: (theme) => `${theme.radius.surface}px`,
        opacity: dimmed ? DIMMED_OPACITY : 1,
      }}
    >
      <Typography
        component="h3"
        sx={{ m: 0, fontSize: "inherit", fontWeight: 600, color: "text.primary" }}
      >
        Parts of {rootName}
      </Typography>
      <Typography component="span" sx={{ fontSize: "inherit" }}>
        Collapsed · {names.slice(0, 3).join(", ")}
        {names.length > 3 ? " …" : ""}
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        <CardLink label="Show the parts side" onClick={onOpenParts}>
          Show ▸
        </CardLink>
      </Box>
    </Box>
  );
};

// The row that stands for the cards of a column not shown: how many, the
// first of them by name, and a control that shows them all
const CollapsedRowView: React.FC<{ row: CollapsedRow; dimmed: boolean }> = ({
  row,
  dimmed,
}) => {
  const { onShowAll } = useChainActions();
  const names = row.cards.map(cardName);
  return (
    <Box
      component="article"
      aria-label={`Not shown in ${row.areaName ? `${row.areaName}, ` : ""}step ${row.step}${row.side === "hosts" ? " of the hosts" : ""}`}
      data-dimmed={dimmed ? "true" : "false"}
      sx={{
        opacity: dimmed ? DIMMED_OPACITY : 1,
        width: 300,
        height: 40,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        px: "14px",
        fontSize: "12.5px",
        color: "text.secondary",
        border: `1px dashed ${DASHED_BORDER}`,
        borderRadius: (theme) => `${theme.radius.surface}px`,
      }}
    >
      <Typography
        component="span"
        noWrap
        title={names.join(", ")}
        sx={{ fontSize: "inherit", minWidth: 0 }}
      >
        <Num>{names.length}</Num> more {names.length === 1 ? "kind" : "kinds"}{" "}
        · {names.slice(0, 3).join(", ")}
        {names.length > 3 ? " …" : ""}
      </Typography>
      <CardLink label="Show all" onClick={() => onShowAll(row.id)}>
        Show ›
      </CardLink>
    </Box>
  );
};

// The box behind the cards of one area in a tier view: the area's name, what
// it holds and its kinds in the column. It takes no pointer: the cards in it
// do.
const AreaBoxView: React.FC<{ area: AreaBox }> = ({ area }) => (
  <Box
    component="section"
    aria-label={`Area ${area.name}`}
    data-area={area.id}
    sx={(theme: Theme) => ({
      width: area.width,
      height: area.height,
      boxSizing: "border-box",
      pointerEvents: "none",
      border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
      borderRadius: `${theme.radius.surface + 4}px`,
      backgroundColor: alpha(theme.palette.common.white, 0.025),
      px: `${AREA_PAD}px`,
      pt: "7px",
    })}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 1,
        height: AREA_HEAD - 7,
        overflow: "hidden",
      }}
    >
      <Typography
        component="h3"
        noWrap
        title={area.name}
        sx={{ m: 0, fontSize: 12.5, fontWeight: 600, color: "text.secondary", minWidth: 0 }}
      >
        {area.name}
      </Typography>
      <Typography
        variant="caption"
        noWrap
        sx={{ color: "text.disabled", flexShrink: 0 }}
      >
        {area.nature ? `${pluralNature(area.nature)} · ` : ""}
        <Num>{area.kinds}</Num> {area.kinds === 1 ? "kind" : "kinds"}
      </Typography>
    </Box>
  </Box>
);

// The words of a subsystem's kinds: those at home, those it shares, and the
// pairs stated from the root into its first level
const SubsystemWords: React.FC<{ subsystem: Subsystem }> = ({ subsystem }) => (
  <Dotted
    parts={[
      <Counted count={subsystem.kinds} noun="kind" />,
      ...(subsystem.shared > 0
        ? [
            <>
              <Num>+{subsystem.shared}</Num> shared
            </>,
          ]
        : []),
      <>
        <Num>{subsystem.statements}</Num> stated
      </>,
    ]}
  />
);

// Whether a subsystem gathers what fits no other: drawn quieter
const quietSubsystem = (subsystem: Subsystem): boolean =>
  subsystem.name === OTHER_SUBSYSTEM || subsystem.name === CARRIED_SUBSYSTEM;

// A closed subsystem of the tree view: its name and its kinds; pressing it
// opens it, which closes the one open before
const SubsystemBoxView: React.FC<{ subsystem: Subsystem }> = ({ subsystem }) => {
  const { onBranch } = useTreeActions();
  return (
    <Box
      component="article"
      aria-label={`Subsystem ${subsystem.name}`}
      className={CONTROL_CLASS}
      onClick={() => onBranch(subsystem.name)}
      sx={(theme: Theme) => ({
        width: 300,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        p: "10px 14px",
        cursor: "pointer",
        backgroundColor: quietSubsystem(subsystem)
          ? theme.palette.background.paper
          : theme.palette.background.raised,
        border: quietSubsystem(subsystem)
          ? `1px dashed ${DASHED_BORDER}`
          : `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.radius.surface}px`,
        "&:hover": { borderColor: alpha(theme.palette.common.white, 0.2) },
      })}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        <Typography
          component="h3"
          noWrap
          title={subsystem.name}
          sx={{ m: 0, minWidth: 0, fontSize: "13.5px", fontWeight: 600 }}
        >
          <Link
            component="button"
            type="button"
            underline="hover"
            aria-label={`Open ${subsystem.name}`}
            aria-expanded={false}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              onBranch(subsystem.name);
            }}
            sx={{ font: "inherit", color: "text.primary", textAlign: "left" }}
          >
            {subsystem.name}
          </Link>
        </Typography>
        <Box component="span" aria-hidden sx={{ color: "text.disabled" }}>
          ▸
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary">
        <SubsystemWords subsystem={subsystem} />
      </Typography>
    </Box>
  );
};

// The box of the open subsystem, behind its cards: its name, its kinds and
// the control that closes it. Only that control takes the pointer. Its head
// starts past the rail, which runs down its right side when mirrored.
const BranchBoxView: React.FC<{
  subsystem: Subsystem;
  width: number;
  height: number;
  mirrored: boolean;
}> = ({ subsystem, width, height, mirrored }) => {
  const { onBranch } = useTreeActions();
  return (
    <Box
      component="section"
      aria-label={`Subsystem ${subsystem.name}`}
      sx={(theme: Theme) => ({
        width,
        height,
        boxSizing: "border-box",
        pointerEvents: "none",
        border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
        borderRadius: `${theme.radius.surface + 4}px`,
        backgroundColor: alpha(theme.palette.common.white, 0.025),
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1.5,
          height: BRANCH_HEAD - 12,
          boxSizing: "border-box",
          pt: "13px",
          ...(mirrored
            ? { pl: 1.5, pr: `${BRANCH_RAIL}px` }
            : { pl: `${BRANCH_RAIL}px`, pr: 1.5 }),
          overflow: "hidden",
        }}
      >
        <Typography
          component="h3"
          noWrap
          sx={{ m: 0, flexShrink: 0, fontSize: "13.5px", fontWeight: 600 }}
        >
          {subsystem.name}
        </Typography>
        <Typography variant="caption" noWrap sx={{ minWidth: 0, color: "text.disabled" }}>
          <SubsystemWords subsystem={subsystem} />
        </Typography>
        <Link
          component="button"
          type="button"
          underline="hover"
          className={CONTROL_CLASS}
          aria-label={`Close ${subsystem.name}`}
          aria-expanded
          onClick={() => onBranch(null)}
          sx={{
            ml: "auto",
            flexShrink: 0,
            fontSize: "12.5px",
            color: "text.secondary",
            pointerEvents: "auto",
          }}
        >
          Close ✕
        </Link>
      </Box>
    </Box>
  );
};

// What the foot of a tree card calls the kinds under it, by the side the
// tree is grown from: its parts, or the kinds it is built into
const TREE_WORDS = {
  parts: {
    noun: "part",
    what: (name: string) => `the parts of ${name}`,
    reading: "reading parts",
    open: "Parts",
  },
  hosts: {
    noun: "host",
    what: (name: string) => `what ${name} is built into`,
    reading: "reading hosts",
    open: "Built into",
  },
} as const;

// The part of a tree card's foot that opens the kinds under it: its parts or
// its hosts in its subsystem, or, at the edge of the read, those read first
const TreeParts: React.FC<{ card: ChainCard; info: TreeCardInfo; name: string }> = ({
  card,
  info,
  name,
}) => {
  const { opened, loading, partsDepth, hostsDepth } = useChainActions();
  const { onTrail } = useTreeActions();
  const self = { type: card.type, id: card.id };
  const read = isOpened(opened, self);
  const words = TREE_WORDS[info.side];
  const depth = info.side === "hosts" ? hostsDepth : partsDepth;
  const spinner = isOpened(loading, self) && (
    <CircularProgress size={10} sx={{ ml: 0.75 }} aria-label="Opening" />
  );
  if (info.placement.shared || isCarried(card)) {
    return null;
  }
  if (info.parts > 0) {
    return (
      <CardLink
        label={`${info.open ? "Close" : "Open"} ${words.what(name)}`}
        expanded={info.open}
        onClick={() => onTrail(info.toggle)}
        colour={info.open ? "text.secondary" : undefined}
      >
        {info.open ? "▾" : "▸"} <Counted count={info.parts} noun={words.noun} />
        {spinner}
      </CardLink>
    );
  }
  // Within the read with nothing beyond it, nothing hangs under it; at its
  // edge, what does is not read yet
  if (read || ((card.kinds_beyond ?? 0) === 0 && card.step < depth)) {
    return spinner ? (
      <Typography component="span" sx={{ fontSize: "inherit", color: "text.disabled" }}>
        {words.reading}
        {spinner}
      </Typography>
    ) : null;
  }
  return (
    <CardLink
      label={`Open ${words.what(name)}`}
      expanded={false}
      onClick={() => onTrail(info.toggle, self)}
    >
      ▸ {words.open}
    </CardLink>
  );
};

// A card of the tree view: its name, what is on record of it and its place.
// Its foot opens its parts; a kind shared with another subsystem points to
// it, and a platform the root carries opens a chain of its own. The number
// at the right is the pairs stated from its host into it. On the path above
// the window it is drawn small, and pressing it moves the window back to it.
const TreeCardView: React.FC<{ card: ChainCard; info: TreeCardInfo }> = ({
  card,
  info,
}) => {
  const { detail, onOpenDetail, onCentre } = useChainActions();
  const { onTrail, onShow } = useTreeActions();
  const { lens, flags } = useChainLens();
  const name = cardName(card);
  const onOpen = () => onOpenDetail({ type: "category", id: card.category_id });
  const detailOpen =
    detail !== null &&
    (detail.type === "kind" ||
      detail.type === "class" ||
      detail.type === "category") &&
    detail.id === card.category_id;
  const ringed = info.open || detailOpen;
  const ruleFlags =
    lens === "jurisdiction" && card.maker_split !== null
      ? (flags.get(card.id) ?? []).filter(isRuleFlag)
      : [];
  const { edge, shared } = info.placement;

  if (info.spine) {
    return (
      <Box
        component="article"
        aria-label={name}
        data-open="true"
        sx={(theme: Theme) => ({
          width: 220,
          minHeight: 44,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: "10px",
          fontSize: "12.5px",
          backgroundColor: theme.palette.background.raised,
          borderRadius: `${theme.radius.surface}px`,
          boxShadow: `0 0 0 1px ${theme.palette.accent.main}`,
        })}
      >
        <CardLink label={`Back to ${name}`} onClick={() => onTrail(info.toggle)}>
          <Box
            component="span"
            sx={{ fontWeight: 600, color: "text.primary", overflowWrap: "anywhere" }}
          >
            {name}
          </Box>
        </CardLink>
        <Typography
          component="span"
          sx={{ ...monospaceStyle, fontSize: 11, color: ACCENT_TEXT, flexShrink: 0 }}
        >
          ▾ {info.parts}
        </Typography>
      </Box>
    );
  }

  const elsewhere = info.elsewhere[0];
  const pointer = elsewhere && (
    <Chip
      size="small"
      variant="outlined"
      className={CONTROL_CLASS}
      label={
        shared
          ? `at home in ${elsewhere} ↗`
          : `also in ${elsewhere}${info.elsewhere.length > 1 ? ` +${info.elsewhere.length - 1}` : ""} ↗`
      }
      title={
        shared
          ? `At home in ${elsewhere}, where its parts are listed`
          : `Also in ${info.elsewhere.join(", ")}`
      }
      aria-label={shared ? `Show ${name} in ${elsewhere}` : `Show ${name} also in ${elsewhere}`}
      onClick={() => onShow(card.id, elsewhere)}
      sx={(theme: Theme) => ({ ...chipSx(true)(theme), maxWidth: 170 })}
    />
  );

  return (
    <Box
      component="article"
      aria-label={name}
      data-open={ringed ? "true" : "false"}
      data-shared={shared ? "true" : "false"}
      sx={(theme: Theme) => ({
        width: 300,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        p: "10px 14px",
        backgroundColor: shared
          ? theme.palette.background.paper
          : theme.palette.background.raised,
        border: "1px solid",
        borderStyle: shared ? "dashed" : "solid",
        borderColor: ringed
          ? "transparent"
          : ruleFlags.length > 0
            ? FLAGGED_BORDER
            : shared
              ? DASHED_BORDER
              : theme.palette.divider,
        borderRadius: `${theme.radius.surface}px`,
        boxShadow: ringed ? `0 0 0 1px ${theme.palette.accent.main}` : "none",
      })}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 1,
        }}
      >
        <Typography
          component="h3"
          sx={{
            m: 0,
            minWidth: 0,
            fontSize: "13.5px",
            fontWeight: 600,
            lineHeight: 1.35,
            overflowWrap: "anywhere",
          }}
        >
          <Link
            component="button"
            type="button"
            underline="hover"
            className={CONTROL_CLASS}
            onClick={onOpen}
            sx={{ font: "inherit", color: "text.primary", textAlign: "left" }}
          >
            {name}
          </Link>
        </Typography>
        <CentreButton card={card} name={name} />
      </Box>
      <CardEvidence card={card} />
      {card.flags.includes(SINGLE_MAKER) && (
        <SingleMakerChip country={card.single_maker_country} />
      )}
      {ruleFlags.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {ruleFlags.map((flag) => (
            <FlagChip key={flag} flag={flag} />
          ))}
        </Box>
      )}
      <Box
        sx={{
          mt: "auto",
          pt: 0.25,
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: "12.5px",
          minWidth: 0,
        }}
      >
        <TreeParts card={card} info={info} name={name} />
        {isCarried(card) && (
          <CardLink
            label={`Centre the chain on ${name}`}
            onClick={() => onCentre({ type: card.type, id: card.id })}
          >
            Its own chain ›
          </CardLink>
        )}
        {pointer}
        <Typography
          component="span"
          title={
            edge.pairs > 0
              ? `${counted(edge.pairs, "pair")} stated inside its host`
              : `${counted(edge.typical, "typical row")} into it from its host`
          }
          sx={{ ...monospaceStyle, ml: "auto", fontSize: 11, color: "text.disabled" }}
        >
          {edge.pairs > 0 ? `${edge.pairs}×` : `~${edge.typical}`}
        </Typography>
      </Box>
    </Box>
  );
};

const HANDLE_STYLE: React.CSSProperties = { opacity: 0, pointerEvents: "none" };

// Where the edge from the root meets the open subsystem's box, and where its
// rail leaves it: on its left edge (its right when mirrored), at its head
const BRANCH_HANDLE_STYLE: React.CSSProperties = {
  ...HANDLE_STYLE,
  top: BRANCH_HANDLE_Y,
};

// The one node type of the chain's canvas. Edges come into a node on its
// left and leave it on its right; on a mirrored node, the other way round.
const ChainCardNode: React.FC<NodeProps<ChainNode>> = ({ data }) => {
  const [into, out] = data.mirrored
    ? [Position.Right, Position.Left]
    : [Position.Left, Position.Right];
  if (data.variant === "area") {
    return <AreaBoxView area={data.area} />;
  }
  if (data.variant === "branch") {
    return (
      <>
        <Handle
          type="target"
          position={into}
          isConnectable={false}
          style={BRANCH_HANDLE_STYLE}
        />
        <BranchBoxView
          subsystem={data.subsystem}
          width={data.width}
          height={data.height}
          mirrored={data.mirrored === true}
        />
        <Handle
          type="source"
          position={into}
          isConnectable={false}
          style={BRANCH_HANDLE_STYLE}
        />
      </>
    );
  }
  let body: React.ReactNode;
  if (data.variant === "card") {
    body = data.tree ? (
      <TreeCardView card={data.card} info={data.tree} />
    ) : (
      <ChainCardView
        card={data.card}
        root={data.root}
        dimmed={data.dimmed}
        pathColour={data.pathColour}
        pathColours={data.pathColours}
        neighbours={data.neighbours}
        compact={data.compact}
        summary={data.summary}
        partsDrawn={data.partsDrawn}
      />
    );
  } else if (data.variant === "subsystem") {
    body = <SubsystemBoxView subsystem={data.subsystem} />;
  } else if (data.row.stub) {
    body = (
      <PartsStubView
        row={data.row}
        rootName={data.rootName}
        dimmed={data.dimmed}
      />
    );
  } else {
    body = <CollapsedRowView row={data.row} dimmed={data.dimmed} />;
  }
  return (
    <>
      <Handle
        type="target"
        position={into}
        isConnectable={false}
        style={HANDLE_STYLE}
      />
      {body}
      <Handle
        type="source"
        position={out}
        isConnectable={false}
        style={HANDLE_STYLE}
      />
    </>
  );
};

export default ChainCardNode;
